import { useState, useCallback, useRef } from 'react';
import { Copy, Check } from 'lucide-react';

interface Props {
  content: string;
}

function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard) {
    return navigator.clipboard.writeText(text).then(() => true).catch(() => false);
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(ta);
  return Promise.resolve(ok);
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [text]);

  return (
    <button className="ai-copy-btn" onClick={handleCopy} title="Копировать">
      {copied ? <Check size={14} /> : <Copy size={14} />}
      <span>{copied ? 'Скопировано' : 'Копировать'}</span>
    </button>
  );
}

function renderInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const italicMatch = remaining.match(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/);
    const codeMatch = remaining.match(/`([^`]+)`/);

    let firstIdx = Infinity;
    let firstType = '';
    let firstMatch: RegExpMatchArray | null = null;

    if (boldMatch && boldMatch.index !== undefined && boldMatch.index < firstIdx) {
      firstIdx = boldMatch.index;
      firstType = 'bold';
      firstMatch = boldMatch;
    }
    if (italicMatch && italicMatch.index !== undefined && italicMatch.index < firstIdx) {
      firstIdx = italicMatch.index;
      firstType = 'italic';
      firstMatch = italicMatch;
    }
    if (codeMatch && codeMatch.index !== undefined && codeMatch.index < firstIdx) {
      firstIdx = codeMatch.index;
      firstType = 'code';
      firstMatch = codeMatch;
    }

    if (!firstMatch) {
      parts.push(remaining);
      break;
    }

    if (firstIdx > 0) {
      parts.push(remaining.slice(0, firstIdx));
    }

    if (firstType === 'bold') {
      parts.push(<strong key={key++}>{firstMatch[1]}</strong>);
    } else if (firstType === 'italic') {
      parts.push(<em key={key++}>{firstMatch[1]}</em>);
    } else if (firstType === 'code') {
      parts.push(<code key={key++} className="ai-inline-code">{firstMatch[1]}</code>);
    }

    remaining = remaining.slice(firstIdx + firstMatch[0].length);
  }

  return parts;
}

function renderMarkdown(content: string): React.ReactNode[] {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trimStart();

    if (trimmed.startsWith('```')) {
      const lang = trimmed.slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++;
      const codeText = codeLines.join('\n');
      elements.push(
        <div key={key++} className="ai-code-block">
          <div className="ai-code-header">
            {lang && <span className="ai-code-lang">{lang}</span>}
            <CopyButton text={codeText} />
          </div>
          <pre><code>{codeText}</code></pre>
        </div>
      );
      continue;
    }

    if (trimmed.startsWith('>')) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].trimStart().startsWith('>')) {
        const raw = lines[i].trimStart();
        const ql = raw.startsWith('> ') ? raw.slice(2) : raw.slice(1);
        quoteLines.push(ql);
        i++;
      }
      const quoteText = quoteLines.join('\n');
      elements.push(
        <blockquote key={key++} className="ai-blockquote">
          <CopyButton text={quoteText} />
          {quoteLines.map((ql, qi) => (
            <p key={qi}>{renderInline(ql)}</p>
          ))}
        </blockquote>
      );
      continue;
    }

    if (trimmed.match(/^---+$/) || trimmed.match(/^\*\*\*+$/) || trimmed.match(/^___+$/)) {
      elements.push(<hr key={key++} className="ai-hr" />);
      i++;
      continue;
    }

    if (trimmed.startsWith('#### ')) {
      elements.push(<h5 key={key++} className="ai-h5">{renderInline(trimmed.slice(5))}</h5>);
      i++;
      continue;
    }

    if (trimmed.startsWith('### ')) {
      elements.push(<h4 key={key++}>{renderInline(trimmed.slice(4))}</h4>);
      i++;
      continue;
    }

    if (trimmed.startsWith('## ')) {
      elements.push(<h3 key={key++}>{renderInline(trimmed.slice(3))}</h3>);
      i++;
      continue;
    }

    if (trimmed.startsWith('# ')) {
      elements.push(<h2 key={key++}>{renderInline(trimmed.slice(2))}</h2>);
      i++;
      continue;
    }

    if (trimmed.match(/^[-*]\s/)) {
      const listItems: string[] = [];
      while (i < lines.length && lines[i].trimStart().match(/^[-*]\s/)) {
        listItems.push(lines[i].trimStart().replace(/^[-*]\s/, ''));
        i++;
      }
      elements.push(
        <ul key={key++}>
          {listItems.map((item, li) => (
            <li key={li}>{renderInline(item)}</li>
          ))}
        </ul>
      );
      continue;
    }

    if (trimmed.match(/^\d+\.\s/)) {
      const listItems: string[] = [];
      while (i < lines.length && lines[i].trimStart().match(/^\d+\.\s/)) {
        listItems.push(lines[i].trimStart().replace(/^\d+\.\s/, ''));
        i++;
      }
      elements.push(
        <ol key={key++}>
          {listItems.map((item, li) => (
            <li key={li}>{renderInline(item)}</li>
          ))}
        </ol>
      );
      continue;
    }

    if (trimmed.startsWith('|')) {
      const tableRows: string[] = [];
      while (i < lines.length && lines[i].trimStart().startsWith('|')) {
        tableRows.push(lines[i].trimStart());
        i++;
      }
      if (tableRows.length >= 2) {
        const headerCells = tableRows[0].split('|').filter(c => c.trim()).map(c => c.trim());
        const dataRows = tableRows.slice(2);
        elements.push(
          <div key={key++} className="ai-table-wrap">
            <table className="ai-table">
              <thead>
                <tr>{headerCells.map((c, ci) => <th key={ci}>{renderInline(c)}</th>)}</tr>
              </thead>
              <tbody>
                {dataRows.map((row, ri) => {
                  const cells = row.split('|').filter(c => c.trim()).map(c => c.trim());
                  return <tr key={ri}>{cells.map((c, ci) => <td key={ci}>{renderInline(c)}</td>)}</tr>;
                })}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }

    if (trimmed === '') {
      i++;
      continue;
    }

    elements.push(<p key={key++}>{renderInline(trimmed)}</p>);
    i++;
  }

  return elements;
}

export default function MarkdownRenderer({ content }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={containerRef} className="ai-markdown">
      {renderMarkdown(content)}
    </div>
  );
}
